/**
 * Central axios interceptors for the admin interface
 *
 * This module provides centralized error handling for axios requests in the admin interface.
 * It handles common scenarios like permission errors, authentication issues, and server errors.
 */

import type { AxiosResponse, AxiosError } from 'axios';

import { lang } from '@/lang/composable';

interface CustomAxiosResponse extends AxiosResponse {
    isHandledError?: boolean;
    errorType?: 'permission' | 'authentication' | 'server' | 'network' | 'not_found';
    originalError?: AxiosError;
}

/**
 * Setup global axios interceptors for admin interface
 */
export const setupAxiosInterceptors = (): void => {
    // Request interceptor - for adding headers, tokens, etc.
    window.axios.interceptors.request.use(
        (config) => {
            // Add any global request headers or modifications here
            return config;
        },
        (error) => {
            return Promise.reject(error);
        },
    );

    // Response interceptor - for handling errors globally
    window.axios.interceptors.response.use(
        (response) => {
            return response;
        },
        (error: AxiosError) => {
            return handleAxiosError(error);
        },
    );
};

/**
 * Handle different types of axios errors
 */
const handleAxiosError = (error: AxiosError): Promise<CustomAxiosResponse> => {
    const status = error.response?.status;

    switch (status) {
        case 403:
            return handlePermissionError(error);
        case 401:
            return handleAuthenticationError(error);
        case 419:
            // CSRF token mismatch - treat as authentication error
            return handleSessionError(error);
        case 404:
            return handleNotFoundError(error);
        case 500:
        case 502:
        case 503:
        case 504:
            return handleServerError(error);
        case undefined:
            // Network error
            return handleNetworkError(error);
        default:
            // Let other errors pass through for normal handling
            return Promise.reject(error);
    }
};

/**
 * Handle 403 Forbidden errors (permission denied)
 */
const handlePermissionError = (error: AxiosError): Promise<CustomAxiosResponse> => {
    console.warn(lang('common::common.errors.access_denied'));

    // Return a resolved promise with custom error structure to prevent Sentry logging
    return Promise.resolve({
        data: {
            success: false,
            message: lang('common::common.errors.access_denied'),
            status: 403,
        },
        status: 403,
        statusText: 'Forbidden',
        headers: error.response?.headers || {},
        config: error.config!,
        isHandledError: true,
        errorType: 'permission',
        originalError: error,
    } as CustomAxiosResponse);
};

/**
 * Handle 401 Unauthorized errors (authentication required)
 */
const handleAuthenticationError = (error: AxiosError): Promise<CustomAxiosResponse> => {
    console.warn(lang('common::common.errors.authentication_required'));

    // Optionally redirect to login page
    // window.location.href = '/login';

    return Promise.resolve({
        data: {
            success: false,
            message: lang('common::common.errors.authentication_required'),
            status: 401,
        },
        status: 401,
        statusText: 'Unauthorized',
        headers: error.response?.headers || {},
        config: error.config!,
        isHandledError: true,
        errorType: 'authentication',
        originalError: error,
    } as CustomAxiosResponse);
};

/**
 * Handle 419 Session Expired errors (CSRF token mismatch)
 */
const handleSessionError = (error: AxiosError): Promise<CustomAxiosResponse> => {
    // Do reload
    window.location.reload();

    return Promise.resolve({
        data: {
            success: false,
            message: lang('common::common.csrf.session_expired'),
            status: 419,
        },
        status: 419,
        statusText: 'Session Expired',
        headers: error.response?.headers || {},
        config: error.config!,
        isHandledError: true,
        errorType: 'authentication',
        originalError: error,
    } as CustomAxiosResponse);
};

/**
 * Handle 404 Not Found errors (resource missing, e.g. deleted between render and request)
 */
const handleNotFoundError = (error: AxiosError): Promise<CustomAxiosResponse> => {
    console.warn(lang('common::common.errors.not_found'));

    return Promise.resolve({
        data: {
            success: false,
            message: lang('common::common.errors.not_found'),
            status: 404,
        },
        status: 404,
        statusText: 'Not Found',
        headers: error.response?.headers || {},
        config: error.config!,
        isHandledError: true,
        errorType: 'not_found',
        originalError: error,
    } as CustomAxiosResponse);
};

/**
 * Handle 5xx server errors
 */
const handleServerError = (error: AxiosError): Promise<CustomAxiosResponse> => {
    console.error(lang('common::common.errors.server_error'), error.response?.status);

    return Promise.resolve({
        data: {
            success: false,
            message: lang('common::common.errors.server_error'),
            status: error.response?.status || 500,
        },
        status: error.response?.status || 500,
        statusText: error.response?.statusText || 'Internal Server Error',
        headers: error.response?.headers || {},
        config: error.config!,
        isHandledError: true,
        errorType: 'server',
        originalError: error,
    } as CustomAxiosResponse);
};

/**
 * Handle network errors (no response received)
 */
const handleNetworkError = (error: AxiosError): Promise<CustomAxiosResponse> => {
    console.error(lang('common::common.errors.network_error'), error?.message || 'Unknown error');

    return Promise.resolve({
        data: {
            success: false,
            message: lang('common::common.errors.network_error'),
            status: 0,
        },
        status: 0,
        statusText: 'Network Error',
        headers: {},
        config: error.config!,
        isHandledError: true,
        errorType: 'network',
        originalError: error,
    } as CustomAxiosResponse);
};

/**
 * Check if a response is a handled error from our interceptors
 */
export const isHandledError = (response: any): response is CustomAxiosResponse => {
    return response && response.isHandledError === true;
};

/**
 * Get the original error from a handled response
 */
export const getOriginalError = (response: CustomAxiosResponse): AxiosError | undefined => {
    return response.originalError;
};
